const transferSteps = [
  'Run a full backup on your current computer using the built-in backup tool',
  'Download and install Family Tree Maker on the new machine',
  'Restore from your saved backup file',
  'Open a few profiles to confirm people and media transferred',
  'Activate your license on the new device',
];

const keyFeatures = [
  { label: 'Pedigree View', desc: 'Classic ancestor layout' },
  { label: 'Family View', desc: 'Households at a glance' },
  { label: 'Person List', desc: 'Navigate your tree fast' },
  { label: 'Individual Details', desc: 'Rich profiles per person' },
  { label: 'Media Gallery', desc: 'Photos and documents together' },
  { label: 'Research Log', desc: 'Track sources and notes' },
];

export default function AdditionalSections() {
  return (
    <>
      <section className="t7-section t7-section--wash">
        <div className="container grid md:grid-cols-2 gap-8 md:gap-12 items-start">
          <div className="t7-head t7-head--rail mb-0">
            <p className="t7-kicker">Any device</p>
            <h2 className="t7-title">Move your research without losing a branch</h2>
          </div>
          <div className="space-y-4 pt-1">
            <p className="text-[#55665c] text-base md:text-lg leading-relaxed">
              Switching from Windows to Mac—or upgrading to a faster computer? Migration tools carry
              your tree, linked media, and notes so nothing slips through the cracks.
            </p>
            <p className="text-[#55665c] leading-relaxed">
              Resume exactly where you left off, on whichever system you choose next.
            </p>
          </div>
        </div>
      </section>

      <section className="t7-section t7-section--white">
        <div className="container">
          <div className="t7-head t7-head--rail">
            <p className="t7-kicker">New computer</p>
            <h2 className="t7-title">Move Family Tree Maker safely</h2>
            <p className="t7-lead">
              Follow this checklist to transfer your tree with confidence—backup, install, restore, verify.
            </p>
          </div>

          <ol className="max-w-3xl space-y-0 border-l-2 border-[#c9956a] ml-2 sm:ml-3 md:ml-4">
            {transferSteps.map((step, i) => (
              <li key={step} className="relative pl-7 sm:pl-8 md:pl-10 pb-6 sm:pb-8 last:pb-0">
                <span className="absolute -left-[13px] sm:-left-[15px] top-0 flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center rounded-full bg-[#2d6a4f] text-white text-[10px] sm:text-xs font-bold">
                  {i + 1}
                </span>
                <p className="text-[#152019] text-sm sm:text-base font-medium leading-relaxed pt-0.5">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="t7-section t7-section--wash">
        <div className="container max-w-3xl">
          <div className="t7-head t7-head--center">
            <p className="t7-kicker">Software overview</p>
            <h2 className="t7-title t7-title--center">What Family Tree Maker does for you</h2>
          </div>
          <div className="space-y-5 text-center px-1">
            <p className="text-[#55665c] text-base md:text-lg leading-relaxed">
              Collect, organize, and share generations of family history with research tools and
              charting that look as polished as they are practical.
            </p>
            <p className="text-[#55665c] text-sm sm:text-base leading-relaxed">
              Build detailed trees, attach media, generate reports, and sync with FamilySearch—
              smart matching and cloud backup keep the journey on track.
            </p>
          </div>
        </div>
      </section>

      <section className="t7-section t7-section--white">
        <div className="container grid md:grid-cols-2 gap-8 md:gap-10 items-start md:items-center">
          <div>
            <div className="t7-head t7-head--rail mb-0">
              <p className="t7-kicker">Latest version</p>
              <h2 className="t7-title">Why upgrade Family Tree Maker?</h2>
            </div>
          </div>
          <div className="space-y-4">
            <p className="text-[#55665c] text-base md:text-lg leading-relaxed">
              New releases bring sharper charting, stronger media tools, and tighter FamilySearch
              integration—while preserving the tree you have already built.
            </p>
            <p className="text-[#55665c] text-sm sm:text-base leading-relaxed">
              Expect cleaner reports, smoother performance, and access to the newest features as
              your research grows.
            </p>
          </div>
        </div>
      </section>

      <section className="t7-section t7-section--wash">
        <div className="container">
          <div className="t7-head t7-head--rail">
            <p className="t7-kicker">Toolbox</p>
            <h2 className="t7-title">Everyday views genealogists rely on</h2>
            <p className="t7-lead">The core screens and tools you will use most often.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
            {keyFeatures.map((feature) => (
              <div key={feature.label} className="flex gap-4">
                <span className="mt-1 h-8 w-[3px] shrink-0 bg-[#c9956a]" aria-hidden="true" />
                <div>
                  <p className="font-serif text-lg text-[#152019] mb-1">{feature.label}</p>
                  <p className="text-sm text-[#55665c]">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
